import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../Components/Navbar';
import Footer from '../Components/Footer';
import { motion } from 'framer-motion';
import { Star, Send, CheckCircle, Shield, Car, Fuel, Wrench, ArrowDownCircle, Phone, BadgeCheck, ChevronLeft, ChevronRight } from 'lucide-react';
import Swal from 'sweetalert2';
import './About.css';
import { API_URL } from '../config';

// ---- Star Rating Component ----
const StarRating = ({ rating, onRate, interactive = false, size = 18 }) => {
  const [hovered, setHovered] = useState(0);
  return (
    <div className="star-rating">
      {[1, 2, 3, 4, 5].map(i => (
        <Star
          key={i}
          size={size}
          fill={(interactive ? (hovered || rating) : rating) >= i ? '#00c2ff' : 'none'}
          stroke={(interactive ? (hovered || rating) : rating) >= i ? '#00c2ff' : 'var(--text-dim)'}
          style={{ cursor: interactive ? 'pointer' : 'default', transition: 'all 0.2s' }}
          onMouseEnter={() => interactive && setHovered(i)}
          onMouseLeave={() => interactive && setHovered(0)}
          onClick={() => interactive && onRate(i)}
        />
      ))}
    </div>
  );
};

// ---- Review Card ----
const ReviewCard = ({ review }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    className="review-card"
  >
    <div className="review-card-header">
      <div className="review-avatar">{review.name?.charAt(0).toUpperCase()}</div>
      <div>
        <div className="review-name">{review.name} {review.surname}</div>
        <div className="review-date">{new Date(review.created_at).toLocaleDateString('tr-TR')}</div>
      </div>
      <div style={{ marginLeft: 'auto' }}>
        <StarRating rating={review.stars} size={14} />
      </div>
    </div>
    <p className="review-text">{review.review_text}</p>
    {review.is_approved === 1 && (
      <div className="review-verified">
        <BadgeCheck size={14} /> Onaylanmış Yorum
      </div>
    )}
  </motion.div>
);

// ---- Review Form ----
const ReviewForm = ({ onSubmit }) => {
  const [name, setName] = useState('');
  const [surname, setSurname] = useState('');
  const [phone, setPhone] = useState('');
  const [comment, setComment] = useState('');
  const [rating, setRating] = useState(0);
  const [kvkkAccepted, setKvkkAccepted] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handlePhoneChange = (e) => {
    // Sadece rakamları al
    const input = e.target.value.replace(/\D/g, '');
    
    // Maksimum 11 hane (0 ile başlıyorsa) veya 10 hane
    const trimmed = input.substring(0, 11);
    
    // Formatlama: 05XX XXX XX XX
    let formatted = trimmed;
    if (trimmed.length > 0) {
      if (trimmed[0] !== '0' && trimmed.length <= 10) {
        // Eğer 0 ile başlamıyorsa başına 0 ekleyebiliriz veya 10 hane sınırı koyarız
        // Genelde kullanıcılar 5xx diye başlar
      }
      
      if (trimmed.length > 3) {
        formatted = `${trimmed.substring(0, 4)} ${trimmed.substring(4, 7)}`;
      }
      if (trimmed.length > 7) {
        formatted = `${formatted} ${trimmed.substring(7, 9)}`;
      }
      if (trimmed.length > 9) {
        formatted = `${formatted} ${trimmed.substring(9, 11)}`;
      }
    }
    
    setPhone(formatted);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Telefon numarası validasyonu (Boşlukları silip kontrol et)
    const rawPhone = phone.replace(/\s/g, '');
    const phoneRegex = /^(05)[0-9][0-9][0-9][0-9][0-9][0-9][0-9][0-9][0-9]$/; // 05 ile başlayan 11 haneli numara
    
    if (!name || !surname || !phone || !comment || !rating || !kvkkAccepted) {
      Swal.fire({
        icon: 'warning',
        title: 'Eksik Bilgi',
        text: 'Lütfen tüm alanları doldurun ve KVKK metnini onaylayın.',
        background: '#0f172a',
        color: '#fff',
        confirmButtonColor: '#00c2ff'
      });
      return;
    }

    if (!phoneRegex.test(rawPhone)) {
      Swal.fire({
        icon: 'error',
        title: 'Geçersiz Telefon',
        text: 'Lütfen geçerli bir telefon numarası giriniz (05XX XXX XX XX).',
        background: '#0f172a',
        color: '#fff',
        confirmButtonColor: '#00c2ff'
      });
      return;
    }
    
    setSubmitted(true);
    const success = await onSubmit({ 
      name, 
      surname, 
      phone: rawPhone, // Veritabanına temizlenmiş haliyle gönder
      review_text: comment, 
      stars: rating 
    });
    
    if (success) {
      setName('');
      setSurname('');
      setPhone('');
      setComment('');
      setRating(0);
      setKvkkAccepted(false);
      setTimeout(() => setSubmitted(false), 4000);
    } else {
      setSubmitted(false);
    }
  };

  return (
    <form className="review-form" onSubmit={handleSubmit}>
      <div className="review-form-header">
        <h3 className="review-form-title">Deneyiminizi Paylaşın</h3>
        <p className="review-form-desc">Hizmetimiz hakkında düşüncelerinizi bizimle paylaşın. Yorumunuz admin onayından sonra yayınlanacaktır.</p>
      </div>

      <div className="review-form-rating">
        <span className="review-form-label">Puanınız</span>
        <StarRating rating={rating} onRate={setRating} interactive size={24} />
      </div>

      <div className="review-form-row">
        <div className="review-field">
          <label className="review-field-label">Ad</label>
          <input
            type="text"
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder="Adınız"
            className="review-field-input"
            required
          />
        </div>
        <div className="review-field">
          <label className="review-field-label">Soyad</label>
          <input
            type="text"
            value={surname}
            onChange={e => setSurname(e.target.value)}
            placeholder="Soyadınız"
            className="review-field-input"
            required
          />
        </div>
      </div>

      <div className="review-field">
        <label className="review-field-label">Telefon Numarası</label>
        <input
          type="text"
          value={phone}
          onChange={handlePhoneChange}
          placeholder="05xx xxx xx xx"
          className="review-field-input"
          required
        />
      </div>

      <div className="review-field">
        <label className="review-field-label">Yorumunuz</label>
        <textarea
          value={comment}
          onChange={e => setComment(e.target.value)}
          placeholder="Deneyiminizi anlatın..."
          className="review-field-input review-field-textarea"
          rows={4}
          required
        />
      </div>

      <div className="review-field-kvkk">
        <label className="kvkk-label">
          <input
            type="checkbox"
            checked={kvkkAccepted}
            onChange={e => setKvkkAccepted(e.target.checked)}
            className="kvkk-checkbox"
            required
          />
          <span>Verilerimin işlenmesini ve <Link to="/kvkk" style={{ color: 'var(--primary)', textDecoration: 'underline' }}>KVKK aydınlatma metnini</Link> onaylıyorum.</span>
        </label>
      </div>

      {submitted ? (
        <div className="review-success">
          <CheckCircle size={18} />
          <span>Yorumunuz gönderildi! Admin onayından sonra yayınlanacaktır.</span>
        </div>
      ) : (
        <button type="submit" className="review-submit-btn">
          <Send size={16} />
          <span>Yorum Gönder</span>
        </button>
      )}
    </form>
  );
};

// ---- Main About Page ----
const REVIEWS_PER_PAGE = 4;

const About = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);

  const fetchReviews = async () => {
    try {
      const response = await fetch(`${API_URL}/reviews`);
      if (response.ok) {
        const data = await response.json();
        setReviews(data);
      }
    } catch (err) {
      console.error('Yorumlar yüklenemedi:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReviews();
  }, []);

  const addReview = async (review) => {
    try {
      const response = await fetch(`${API_URL}/reviews`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(review)
      });
      
      if (response.ok) {
        Swal.fire({
          icon: 'success',
          title: 'Teşekkürler!',
          text: 'Yorumunuz başarıyla gönderildi, onay sonrası yayınlanacaktır.',
          background: '#0f172a',
          color: '#fff',
          confirmButtonColor: '#00c2ff'
        });
        return true;
      } else {
        throw new Error('Gönderim hatası');
      }
    } catch (err) {
      Swal.fire({
        icon: 'error',
        title: 'Hata',
        text: 'Yorum gönderilirken bir sorun oluştu.',
        background: '#0f172a',
        color: '#fff'
      });
      return false;
    }
  };

  const approvedReviews = reviews; // Backend already filters for approved ones
  const avgRating = approvedReviews.length > 0
    ? (approvedReviews.reduce((sum, r) => sum + r.stars, 0) / approvedReviews.length).toFixed(1)
    : 0;

  // Pagination logic
  const totalPages = Math.ceil(approvedReviews.length / REVIEWS_PER_PAGE);
  const startIndex = (currentPage - 1) * REVIEWS_PER_PAGE;
  const paginatedReviews = approvedReviews.slice(startIndex, startIndex + REVIEWS_PER_PAGE);

  const goToPage = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
    // Scroll to reviews section smoothly
    const el = document.getElementById('yorumlar');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const advantages = [
    { icon: <Fuel size={22} />, title: 'Gider Gösterme', desc: 'Araçla ilgili tüm masrafları gider gösterebilirsiniz.' },
    { icon: <Shield size={22} />, title: 'Vergi Avantajı', desc: 'Sigorta, kasko, MTV, bandrol gibi vergilerle uğraşmazsınız.' },
    { icon: <Wrench size={22} />, title: 'Bakım Dahil', desc: 'Periyodik bakım, arıza gibi masraflar size ait değildir.' },
    { icon: <Car size={22} />, title: 'Model Değişikliği', desc: 'İhtiyacınıza göre araç model değişikliği yapabilirsiniz.' },
    { icon: <CheckCircle size={22} />, title: 'Araçsız Kalmayın', desc: 'Kaza veya arıza durumunda araçsız kalmazsınız.' },
    { icon: <ArrowDownCircle size={22} />, title: 'Risk Yok', desc: 'Amortisman, ikinci el değer kaybı gibi riskleri üstlenmezsiniz.' },
  ];

  const requirements = [
    'En az 23 yaşında olmak',
    'Minimum 5 yıllık sürücü belgesine sahip olmak',
    'SGK veya Bağ-Kur kaydı bulunmak'
  ];

  return (
    <div style={{ backgroundColor: 'var(--bg-main)', minHeight: '100vh', position: 'relative' }}>
      <Navbar />

      <main style={{ paddingTop: '160px' }}>
        {/* Hero */}
        <section className="about-hero">
          <div className="ambient-light" style={{ top: '-10%', left: '-10%', opacity: 0.12 }}></div>
          <div className="container">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1 }}
            >
              <div className="about-badge">Hakkımızda</div>
              <h1 className="section-title" style={{ marginBottom: '24px' }}>ERENİS<span>Otomotiv.</span></h1>
            </motion.div>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.8 }}
              className="about-hero-text"
            >
              ERENİS Otomotiv Rent A Car olarak, bireysel ve kurumsal müşterilerimize kısa ve uzun dönem araç kiralama hizmetleri sunmaktayız. 2023 ve üzeri model, ekonomik, orta sınıf ve lüks araç seçeneklerimizle, ihtiyaçlarınıza uygun çözümler üretiyoruz.
            </motion.p>
          </div>
        </section>

        {/* Why Erenis */}
        <section className="about-section">
          <div className="container">
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
            >
              <div className="about-section-label">🚗 Neden ERENİS?</div>
              <h2 className="about-section-title">Fark Yaratan Detaylar</h2>
            </motion.div>
            <div className="about-features-grid">
              {[
                'Tüm araçlarımız Rent a Car kaskolu ve düzenli bakımlıdır.',
                'Haftalık, aylık ve yıllık kiralama seçeneklerimiz mevcuttur.',
                'Fiyatlarımıza KDV dahildir – sürpriz maliyet yoktur.',
                'Kurumsal müşteriler için uzun dönem araç kiralama fırsatları sunuyoruz.'
              ].map((item, i) => (
                <motion.div
                  key={i}
                  className="about-feature-card"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                >
                  <div className="about-feature-check"><CheckCircle size={18} /></div>
                  <p>{item}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Requirements */}
        <section className="about-section about-section-dark">
          <div className="container">
            <div className="about-requirements-grid">
              <div>
                <div className="about-section-label">✅ Araç Kiralama Şartları</div>
                <h2 className="about-section-title">Başvuru Koşulları</h2>
                <div className="about-req-list">
                  {requirements.map((req, i) => (
                    <div key={i} className="about-req-item">
                      <div className="about-req-number">0{i + 1}</div>
                      <span>{req}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="about-req-image">
                <img
                  src="https://images.unsplash.com/photo-1549317661-bd32c8ce0db2?auto=format&fit=crop&q=80&w=800"
                  alt="Erenis Fleet"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Why Rent */}
        <section className="about-section">
          <div className="container">
            <div className="about-section-label">🎯 Neden Kiralama?</div>
            <h2 className="about-section-title">Satın Alma Yerine Kiralama Avantajları</h2>
            <div className="about-advantages-grid">
              {advantages.map((adv, i) => (
                <motion.div
                  key={i}
                  className="about-advantage-card"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.08 }}
                >
                  <div className="about-advantage-icon">{adv.icon}</div>
                  <h4>{adv.title}</h4>
                  <p>{adv.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Support & Corporate */}
        <section className="about-section about-section-dark">
          <div className="container">
            <div className="about-support-grid">
              <div className="about-support-card">
                <div className="about-support-icon"><Phone size={28} /></div>
                <h3>📞 7/24 Destek</h3>
                <p>Araçla ilgili herhangi bir sorun ya da ihtiyacınızda 7 gün 24 saat destek sunuyoruz.</p>
                <a href="tel:+905321585646" className="about-phone-link">
                  📱 0532 158 56 46
                </a>
              </div>
              <div className="about-support-card">
                <div className="about-support-icon"><Car size={28} /></div>
                <h3>🏢 Kurumsal Kiralama</h3>
                <p>Kurumsal firmalar için sunduğumuz yıllık kiralama paketlerinde, belirtilen fiyat aylık bazda öngörülen tutardır.</p>
                <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem', marginTop: '12px' }}>
                  Detaylı bilgi ve diğer araç seçeneklerimiz için bizimle iletişime geçebilirsiniz.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Reviews Section */}
        <section className="about-section" id="yorumlar">
          <div className="container">
            <div className="about-section-label">⭐ Müşteri Yorumları</div>
            <h2 className="about-section-title">Müşterilerimiz Ne Diyor?</h2>

            {/* Rating Summary */}
            <div className="review-summary">
              <div className="review-summary-score">{avgRating}</div>
              <div>
                <StarRating rating={Math.round(avgRating)} size={20} />
                <div className="review-summary-count">{approvedReviews.length} onaylanmış yorum</div>
              </div>
            </div>

            {/* Review Cards */}
            <div className="review-grid">
              {paginatedReviews.map((review, i) => (
                <ReviewCard key={`${currentPage}-${i}`} review={review} />
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="review-pagination">
                <button
                  className="review-pagination-btn"
                  onClick={() => goToPage(currentPage - 1)}
                  disabled={currentPage === 1}
                  aria-label="Önceki sayfa"
                >
                  <ChevronLeft size={18} />
                </button>

                <div className="review-pagination-pages">
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                    <button
                      key={page}
                      className={`review-pagination-page ${currentPage === page ? 'active' : ''}`}
                      onClick={() => goToPage(page)}
                    >
                      {page}
                    </button>
                  ))}
                </div>

                <button
                  className="review-pagination-btn"
                  onClick={() => goToPage(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  aria-label="Sonraki sayfa"
                >
                  <ChevronRight size={18} />
                </button>
              </div>
            )}

            {/* Review Form */}
            <ReviewForm onSubmit={addReview} />
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default About;
